import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, inject, Injectable } from '@angular/core';
import { collection, Timestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { BroadcastPayload, BroadcastResult } from '@pages/broadcast/broadcast.config';
import { CollectionType, Message } from '@pages/consorcios/interaction.abstract';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { AdministrationUser, ConnectionWsp } from 'src/app/models/user';
import { environment } from 'src/environments/environment';
import { FirebaseAuthProvider } from '@shared/modules/auth/providers/firebase';
import { getAvailableMessages, isNoMessageBalanceResponse, NoMessageBalanceError } from 'src/app/models/message-balance';
import { WspDisconnectedError, WspSendBlockedError } from 'src/app/models/wsp-send-errors';
import { WSP_DISCONNECTED_TEXT, WspStatusService } from '@shared/services/wsp-status.service';


/* status: 'connecting' |   */

/* Número desde el que se envían los mensajes. Con Meta es el administrationWsp de la cuenta */
export interface WspSender {
  instanceName: string;
}

/* Respuesta de los webhooks de n8n (enviar-mensaje, mute, unmute). Sin saldo, error trae { available, required } */
export interface WspResponse {
  success: boolean;
  error?: any;
}

export type StatusConnection = 'connecting' | 'open' | 'close' | undefined;

@Injectable({
  providedIn: 'root'
})
export class ChatsService {


  private basePath = environment.agentePath;
  public statusConnection: StatusConnection = undefined;
  private authProvider = inject(FirebaseAuthProvider);
  //estado real de la conexión de WhatsApp (AF327): no se manda si está desconectado
  private wspStatus = inject(WspStatusService);

  constructor(private http: HttpClient,@Inject(DOCUMENT) private readonly documentRef: Document) { }

  getChatsWsp(wspId:string,administrationUserId:string,connectionWsp: ConnectionWsp,collection:CollectionType, collectionId:string,administrationWsp?:string):Observable<Message[]>{
    let sessionId=wspId+"-";
    switch(connectionWsp){
        case 'icloudmeta':
        default:
          sessionId+=administrationWsp.replace(/\+/g, '%2B');
        break;
      }
    let endpoint = `https://${environment.chatsEndpoint}.supabase.co/functions/v1/get-chat-history?session_id=${sessionId}&limit=100&context_window=5`;
    if(collection && collectionId){
      endpoint += `&collection=${collection}&collectionId=${collectionId}`;
    }
  
    return this.http.get<Message[]>(endpoint,{
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + environment.chatsEndpointKey,
            'x-api-key': environment.chatsAPIKEY
        }),
    }).pipe(
      //al agregar el context_window parece que viene al revez, pero solo aplica si hay un collectionId
      //invertir el orden de los mensajes para que el mas nuevo quede abajo
      map(messages => {
        if(!collectionId){
          messages['messages'].reverse();
        }
        return messages;
      }),
      catchError(error => {
        // console.error('Error fetching chat history:', error);
        // return of([]);
        throw new Error('Error fetching chat history: ' + error.message);
      })
    );
  }

  getChatsWspGroupByWspId(administrationUserId:string,connectionWsp: ConnectionWsp,filter:{preregistry:boolean,lastHuman:boolean},administrationWsp?:string):Observable<Message[]>{
    let adminId="";
    switch(connectionWsp){
        case 'icloudmeta':
        default:
          adminId=administrationWsp.replace(/\+/g, '%2B');
        break;
      }
    let endpoint = `https://${environment.chatsEndpoint}.supabase.co/functions/v1/get-chat-history?grouped=true&admin_id=${adminId}`;
    if(filter?.preregistry){
      endpoint += `&collection=preRegistry`;
    }
    if(filter?.lastHuman){
      endpoint += `&type=human`;
    }
    return this.http.get<Message[]>(endpoint,{
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + environment.chatsEndpointKey,
            'x-api-key': environment.chatsAPIKEY
        }),
    }).pipe(
      map(data => {
        //nos quedamos solo con el numero del usuario
        data['messages'].forEach(msg => {
          if(msg.session_id){
            msg.session_id = msg.session_id.split('@')[0];
          }
        });
        return data['messages'];
      }),
      catchError(error => {
        // console.error('Error fetching chat history:', error);
        // return of([]);
        throw new Error('Error fetching chat history: ' + error.message);
      })
    );
  }


  

  sendWspMessage(wspId:string, message: Message,wspResponse: WspSender, connectionWsp: ConnectionWsp,silence:boolean=false, attachment?: string){
    
    const formData = new FormData();
    formData.append('instanceName', wspResponse.instanceName);
    formData.append('chatId', wspId);
    formData.append('message', message.content);
    formData.append('collection', message.collection);
    formData.append('collectionId', message.collectionId);
    formData.append('newMember', silence.toString());
    
    if(attachment){
      formData.append('file', attachment);
    }

    switch(connectionWsp){
      case 'icloudmeta':
      default:
        formData.append('apikey', 'dummy');
        formData.append('fromNumber', wspResponse.instanceName);
      break;
    }

    const endpoint = this.basePath + 'enviar-mensaje';
    return this.http.post<WspResponse>(endpoint,formData , {
      headers: new HttpHeaders({
          /* 'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + environment.chatsEndpointKey, */
      }),
    });
  }

  sendWameMessage(recipientNumber:string,message?:string){
    this.documentRef.defaultView.open(`https://wa.me/${recipientNumber}?text=${encodeURIComponent(message ?? '')}`, "_blank");
  }

  /* Control previo del saldo de mensajes (el control real lo hace n8n al enviar).
     Si la cuenta todavía no tiene messageBalance no se controla: el saldo se carga recién con el primer
     cobro de la suscripción o compra de paquete después del deploy, y bloquearla dejaría sin envíos a todos */
  private async checkMessageBalance(required: number = 1){
    const user = await this.authProvider.getCurrentUser() as unknown as AdministrationUser;
    if(!user?.messageBalance){
      return;
    }
    const available = getAvailableMessages(user.messageBalance);
    if(available < required){
      throw new NoMessageBalanceError(available, required);
    }
  }

  /* Si n8n respondió que no hay saldo, lo convierte en NoMessageBalanceError */
  private checkNoBalanceResponse(response: any){
    if(isNoMessageBalanceResponse(response)){
      throw new NoMessageBalanceError(response.error.available ?? 0, response.error.required ?? 1);
    }
    return response;
  }

  /* Control previo de la conexión (AF327): si el estado guardado es desconectado se vuelve a consultar en el momento;
     si sigue desconectado (o sin configurar) no se manda. Sin dato o sin suscripción no se bloquea acá */
  private async checkWspConnection(){
    if(!(await this.wspStatus.ensureCanSend())){
      const reason = this.wspStatus.reason();
      throw new WspDisconnectedError(reason === WSP_DISCONNECTED_TEXT ? '' : reason);
    }
  }

  /* Manda y, si el envío falla (no por saldo ni por conexión), vuelve a consultar el estado: puede haberse
     desconectado entre revisiones (AF327, caso "caída entre revisiones") */
  private async sendAndWatch<T>(send: () => Promise<T>): Promise<T>{
    try{
      const response: any = await send();
      if(response?.success === false && !isNoMessageBalanceResponse(response)){
        this.wspStatus.onSendFailed();
      }
      return response;
    }catch(error){
      if(!(error instanceof WspSendBlockedError)){
        this.wspStatus.onSendFailed();
      }
      throw error;
    }
  }

  async prepareWspMessage(wspId:string, message: Message,wspResponse: WspSender,connectionWsp: ConnectionWsp, attachment?: string){

    await this.checkMessageBalance();
    await this.checkWspConnection();

    //revisar si esta conectado
    switch(connectionWsp){
      case 'icloudmeta':
      default:
        return this.sendAndWatch(async () => this.checkNoBalanceResponse(await firstValueFrom(this.sendWspMessage(wspId, message, wspResponse,connectionWsp,false, attachment))));
      break;
      case 'none':
        throw new Error('No WhatsApp connection method selected. Please select a connection method to send messages.');
      break;
    }
  }

  async prepareUnmuteWithMessage(wspId:string, message: Message,wspResponse: WspSender,connectionWsp: ConnectionWsp){
    await this.checkMessageBalance();
    await this.checkWspConnection();

    //revisar si esta conectado
    switch(connectionWsp){
      case 'icloudmeta':
      default:
        return this.sendAndWatch(async () => this.checkNoBalanceResponse(await firstValueFrom(this.sendWspMessage(wspId, message, wspResponse,connectionWsp,true))));
      break;
      case 'none':
        throw new Error('No WhatsApp connection method selected. Please select a connection method to send messages.');
      break;
    }
  }

  muteChat(wspId:string,time:number = 600){
    const endpoint = this.basePath + 'mute';
    return this.http.post<WspResponse>(endpoint, {
      "wspId": wspId,
      "time": time,
    }, {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'/* ,
            'Authorization': 'Bearer ' + environment.chatsEndpointKey, */
        }),
    });
  }

  unmuteChat(wspId:string){
    const endpoint = this.basePath + 'unmute';
    return this.http.post<WspResponse>(endpoint, {
                    "wspId": wspId,
    }, {
      headers: new HttpHeaders({
          'Content-Type': 'application/json'/* ,
          'Authorization': 'Bearer ' + environment.chatsEndpointKey, */
      }),
    });
  }


  async sendBroadcastMessage(payload: BroadcastPayload): Promise<BroadcastResult> {
    try {
      //cada numero es un mensaje
      await this.checkMessageBalance(payload.numbers.length);
      await this.checkWspConnection();
      const endpoint = `${this.basePath}enviar-difusion`;
      return await this.sendAndWatch(async () => this.checkNoBalanceResponse(await firstValueFrom(this.http.post<BroadcastResult>(endpoint, payload))));
    } catch (error) {
      console.error('Error sending broadcast message:', error);
      throw error;
    }
  }
}
